import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeCompanyUsers,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useSOLDNotifications } from '../AutomationSoLDSettingsSlideout/use-SOLD-notifications';
import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import { useConnectSlack } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SlackIntegrations/use-connect-slack';
import { useFormik } from 'formik';
import { useDesktopNotifications } from '../../../../../common/hooks/use-desktop-notifications/use-desktop-notifications';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import {
  CampaignType,
  SoldConfigurationsType,
} from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { isEqual } from 'lodash';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { equals } from 'lodash/fp';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import css from '../AutomationSalesTeamNotifications/AutomationSalesTeamNotifications.module.css';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  AutomationReportingEventSelector,
  getCampaignActiveReportingOptionId,
  getCampaignSoldReportingOptionId,
  getIsCampaignReportingStatusActive,
  getIsRelatedCampaignReportingStatusActive,
  HandIcon,
  HotLeadIcon,
} from '../../../../features/beta/modules/Automations/AutomationEdit/AutomationEditEmailSold';
import { Heading, Switch, Tooltip } from '@mm/components';
import { DropdownOption } from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { CloseOutlined, SettingOutlined } from '@ant-design/icons';
import { ReactComponent as SlackIcon } from 'assets/slack.svg';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { EmailRed, SmsNotificationIcon } from '@mm/icons';
// import './AutomationHotLeadAutomation.pcss';

export interface IAutomationHotLeadAutomation {
  menuType: SlideOutMenuType;
  campaignId?: number;
  className?: string;
}

export const AutomationHotLeadAutomation = ({
  menuType,
  campaignId,
  className,
}: IAutomationHotLeadAutomation) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);

  const campaign = useSelector(state =>
    selectCampaignById(state, campaignId || -1),
  );

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;

  const modalActions = useModalActions();

  const viewActions = useViewActions();

  const { setRef, close: closeSlideout } = useSlideoutOnClose(async () => {},
  menuType);

  const companyUserIdsToNotify: number[] = [];

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const [isActive, setIsActive] = useState(
    getIsRelatedCampaignReportingStatusActive(campaign),
  );

  const getSwitchCss = (type?: string) => {
    return {
      container: isActive ? css.contentSwitchActive : css.contentSwitchInactive,
      thumb: isActive ? css.contentThumbActive : css.contentThumbInactive,
      label: isActive
        ? css.contentSwitchLabelActive
        : css.contentSwitchLabelInactive,
    };
  };

  const [event, setEvent] = useState<string | number>(
    getCampaignSoldReportingOptionId(campaign) || '',
  );
  const handleEventSelectorChange = (opt: DropdownOption) => {
    setEvent(opt.id);
  };

  const withActivation = (sold?: typeof soldConfigInitialValues) => {
    const clone = JSON.parse(JSON.stringify(sold));

    clone.unicorn.open = 0;
    clone.unicorn.reply = 0;
    clone.unicorn.click = 0;
    clone.unicorn.defaultConfiguration = false;

    if (isActive && event in clone.unicorn) {
      clone.unicorn[event] = 1;
    }

    return clone as SoldConfigurationsType;
  };

  const onSave = async () => {
    setBusy(true);
    try {
      if (campaignId) {
        const soldConfig = withActivation(
          campaign?.soldConfigurations || soldConfigInitialValues,
        );

        const res = await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: soldConfig,
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleChange = () => {
    setIsActive(!isActive);
  };

  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setDirty(true);
    } else {
      onSave();
    }
  }, [event, isActive]);

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <HotLeadIcon className={css.icon} color="#389E0D" />
        <Heading size={3}>New Hot Lead Automation</Heading>
        {/* Might be brought back */}
        {/*<Button*/}
        {/*  css={{ container: css.save }}*/}
        {/*  onClick={onSave}*/}
        {/*  disabled={isSaveDisabled}*/}
        {/*>*/}
        {/*  Save*/}
        {/*</Button>*/}
        <span className={css.close} onClick={closeSlideout}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <Switch
          checked={isActive}
          label="Activate Hot Lead Automation"
          disabled={busy}
          onChange={handleToggleChange}
          css={{
            container: css.switch,
            thumb: isActive ? css.thumbActive : css.thumbInactive,
            label: isActive ? css.switchLabelActive : css.switchLabelInactive,
          }}
        />

        <div className={css.selectorContainer}>
          <span>If a lead</span>
          <AutomationReportingEventSelector
            value={event}
            disabled={!isActive || busy}
            onChange={handleEventSelectorChange}
          />
        </div>
      </Menu.Content>
    </Menu>
  );
};
